import api from '../axios';
import authService from './authService';

/**
 * Serviço para dados de consumo
 */
class ConsumosService {

  /**
   * Verifica autenticação antes de fazer requisições
   * @returns {boolean} Status de autenticação
   */
  verificarAutenticacao() {
    if (!authService.isAuthenticated()) {
      console.warn('Usuário não autenticado para acessar dados de consumo');
      return false;
    }
    return true;
  }

  async obterConsumoDiario(dataRef) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        success: false,
        error: 'Usuário não autenticado',
        data: null
      };
    }

    try {
      const response = await api.get('/api/consumos/diario', {
        params: {
          dataRef
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao buscar consumo diário:', error);
      
      // // Fallback com dados mockados
      // const dadosFallback = this.gerarDadosMockados();
      
      // return {
      //   success: false,
      //   error: error.message || 'Erro ao carregar dados de consumo diário',
      //   data: dadosFallback
      // };
    }
  }

  /**
   * Busca dados de consumo mensal agregado
   * @param {string} dataRef - Mês de referência no formato MM/YYYY
   * @param {string} unidadeConsumidora - ID da unidade consumidora
   * @returns {Promise} Dados do consumo mensal
   */
  async obterConsumoMensal(dataRef, unidadeConsumidora) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        success: false,
        error: 'Usuário não autenticado',
        data: null
      };
    }

    try {
      const response = await api.get('/api/consumo/mensal', {
        params: {
          dataRef,
          unidadeConsumidora
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao buscar consumo mensal:', error);
      
      // Fallback com dados mockados
      const dadosFallback = {
        consumo_total: 450.75,
        consumo_pico: 120.30,
        consumo_economico: 200.15,
        consumo_super_economico: 130.30,
        mes_referencia: dataRef,
        dias_faturados: 30,
        media_consumo: 15.025,
        unidade_consumidora: unidadeConsumidora
      };
      
      return {
        success: false,
        error: error.message || 'Erro ao carregar dados de consumo mensal',
        data: dadosFallback
      };
    }
  }

  /**
   * Busca histórico de consumo (últimos 12 meses)
   * @param {string} unidadeConsumidora - ID da unidade consumidora
   * @returns {Promise} Histórico de consumo
   */
  async obterHistoricoConsumo(unidadeConsumidora) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        success: false,
        error: 'Usuário não autenticado',
        data: null
      };
    }

    try {
      const response = await api.get('/api/consumo/historico', {
        params: {
          unidadeConsumidora
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Erro ao buscar histórico de consumo:', error);
      
      // Fallback com dados mockados de histórico
      const dadosFallback = {
        historico: Array.from({ length: 12 }, (_, i) => {
          const data = new Date();
          data.setMonth(data.getMonth() - i);
          return {
            mes: data.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }),
            consumo_total: Math.random() * 200 + 300,
            consumo_pico: Math.random() * 50 + 80,
            consumo_economico: Math.random() * 100 + 150,
            consumo_super_economico: Math.random() * 80 + 70
          };
        }).reverse(),
        unidade_consumidora: unidadeConsumidora
      };
      
      return {
        success: false,
        error: error.message || 'Erro ao carregar histórico de consumo',
        data: dadosFallback
      };
    }
  }

  /**
   * Transforma os dados da API no formato esperado pelo componente de gráfico
   * @param {Object} apiData - Dados vindos da API
   * @returns {Object} Dados formatados para o componente
   */
  formatarDadosConsumoDiario(apiData) {
    if (!apiData || !Array.isArray(apiData.dados_diarios)) return null;

    return {
      consumoDiario: apiData.dados_diarios.map((dia,i) => ({
        dia: dia.dia || dia.day,
        consumoPico: parseFloat(dia.consumo_pico || dia.consumoPico || 0),
        consumoEconomico: parseFloat(dia.consumo_economico || dia.consumoEconomico || 0),
        consumoSuperEconomico: parseFloat(dia.consumo_super_economico || dia.consumoSuperEconomico || 0),
        isEstimado: dia.is_estimado || dia.isEstimado || false
      })),
      consumoTotal: parseFloat(apiData.consumo_total || apiData.consumoTotal || 0),
      dataRef: apiData.mes_referencia || apiData.dataRef,
      unidadeConsumidora: apiData.unidade_consumidora || apiData.unidadeConsumidora
    };
  }

  /**
   * Formatar dados do consumo mensal
   * @param {Object} apiData - Dados vindos da API
   * @returns {Object} Dados formatados
   */
  formatarDadosConsumoMensal(apiData) {
    if (!apiData) return null;

    return {
      consumoTotal: parseFloat(apiData.consumo_total || apiData.consumoTotal || 0),
      consumoPico: parseFloat(apiData.consumo_pico || apiData.consumoPico || 0),
      consumoEconomico: parseFloat(apiData.consumo_economico || apiData.consumoEconomico || 0),
      consumoSuperEconomico: parseFloat(apiData.consumo_super_economico || apiData.consumoSuperEconomico || 0),
      dataRef: apiData.mes_referencia || apiData.dataRef,
      diasFaturados: parseInt(apiData.dias_faturados || apiData.diasFaturados || 30),
      mediaConsumo: parseFloat(apiData.media_consumo || apiData.mediaConsumo || 0)
    };
  }

  /**
   * Gerar dados mockados para desenvolvimento/fallback
   * @param {number} dias - Número de dias para gerar (padrão: 31)
   * @returns {Object} Dados mockados formatados
   */
  gerarDadosMockados(dias = 31) {
    // Gerar 3 dias aleatórios para serem estimados
    const diasEstimados = new Set();
    while (diasEstimados.size < 3) {
      diasEstimados.add(Math.floor(Math.random() * dias) + 1);
    }

    const consumoDiario = Array.from({ length: dias }, (_, i) => ({
      dia: i + 1,
      consumoPico: Math.random() * 8 + 1,
      consumoEconomico: Math.random() * 12 + 3,
      consumoSuperEconomico: Math.random() * 15 + 5,
      isEstimado: diasEstimados.has(i + 1),
    }));

    const consumoTotal = consumoDiario.reduce((total, dia) => 
      total + dia.consumoPico + dia.consumoEconomico + dia.consumoSuperEconomico, 0
    );

    return {
      consumoDiario,
      consumoTotal: Math.round(consumoTotal * 100) / 100,
      dataRef: new Date().toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric' }),
      unidadeConsumidora: null
    };
  }

  /**
   * Calcular estatísticas do consumo diário
   * @param {Array} consumoDiario - Array com dados de consumo diário
   * @returns {Object} Estatísticas calculadas
   */
  calcularEstatisticas(consumoDiario) {
    if (!Array.isArray(consumoDiario) || consumoDiario.length === 0) {
      return {
        consumoMedio: 0,
        consumoMaximo: 0,
        consumoMinimo: 0,
        diasComConsumo: 0,
        percentualPico: 0,
        percentualEconomico: 0,
        percentualSuperEconomico: 0
      };
    }

    const consumos = consumoDiario.map(dia => 
      dia.consumoPico + dia.consumoEconomico + dia.consumoSuperEconomico
    );

    const consumoTotal = consumos.reduce((sum, consumo) => sum + consumo, 0);
    const consumoPicoTotal = consumoDiario.reduce((sum, dia) => sum + dia.consumoPico, 0);
    const consumoEconomicoTotal = consumoDiario.reduce((sum, dia) => sum + dia.consumoEconomico, 0);
    const consumoSuperEconomicoTotal = consumoDiario.reduce((sum, dia) => sum + dia.consumoSuperEconomico, 0);

    return {
      consumoMedio: consumoTotal / consumoDiario.length,
      consumoMaximo: Math.max(...consumos),
      consumoMinimo: Math.min(...consumos),
      diasComConsumo: consumos.filter(c => c > 0).length,
      percentualPico: consumoTotal > 0 ? (consumoPicoTotal / consumoTotal) * 100 : 0,
      percentualEconomico: consumoTotal > 0 ? (consumoEconomicoTotal / consumoTotal) * 100 : 0,
      percentualSuperEconomico: consumoTotal > 0 ? (consumoSuperEconomicoTotal / consumoTotal) * 100 : 0
    };
  }

  /**
   * Obtém dados de consumo dentro de um período
   * @param {string} dataIni - Data inicial (formato: YYYY-MM-DDTHH:mm:ss)
   * @param {string} dataFim - Data final (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com dados de consumo
   */
  async getConsumos(dataIni, dataFim) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        data: { error: 'Usuário não autenticado' },
        status: 401
      };
    }

    try {
      const response = await api.get('/api/consumos', {
        params: {
          DataIni: dataIni,
          DataFim: dataFim
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar consumos:', error);
      
      // Fallback com dados mockados
      const dadosFallback = {
        data: {
          consumos: Array.from({ length: 7 }, (_, i) => ({
            data: new Date(Date.now() + i * 24 * 60 * 60 * 1000).toISOString(),
            consumo_pico: Math.random() * 10 + 5,
            consumo_economico: Math.random() * 15 + 10,
            consumo_super_economico: Math.random() * 12 + 8
          })),
          periodo: {
            inicio: dataIni,
            fim: dataFim
          }
        },
        status: 200
      };
      
      return dadosFallback;
    }
  }

  /**
   * Obtém dados detalhados de consumo para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com dados detalhados de consumo
   */
  async getConsumosDetalhados(dataRef) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        data: { error: 'Usuário não autenticado' },
        status: 401
      };
    }

    try {
      const response = await api.get('/api/consumos/detalhados', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar consumos detalhados:', error);
      
      // Fallback com dados mockados detalhados
      const dadosFallback = {
        data: {
          consumo_detalhado: {
            consumo_por_hora: Array.from({ length: 24 }, (_, i) => ({
              hora: i,
              consumo: Math.random() * 5 + 1,
              faixa_tarifaria: i >= 17 && i <= 20 ? 'pico' : i >= 21 || i <= 6 ? 'super_economico' : 'economico'
            })),
            total_dia: Math.random() * 50 + 30,
            data_referencia: dataRef
          }
        },
        status: 200
      };
      
      return dadosFallback;
    }
  }

  /**
   * Obtém indicadores de consumo para uma data específica
   * @param {string} dataRef - Data de referência (formato: YYYY-MM-DDTHH:mm:ss)
   * @returns {Promise} Response da API com indicadores de consumo
   */
  async getIndicadores(dataRef) {
    // Verifica autenticação antes de prosseguir
    if (!this.verificarAutenticacao()) {
      return {
        data: { error: 'Usuário não autenticado' },
        status: 401
      };
    }

    try {
      const response = await api.get('/api/consumos/indicadores', {
        params: {
          DataRef: dataRef
        }
      });
      return response;
    } catch (error) {
      console.error('Erro ao buscar indicadores de consumo:', error);
      
      // Fallback com indicadores mockados
      const dadosFallback = {
        data: {
          indicadores: {
            consumo_medio_diario: Math.random() * 20 + 15,
            pico_consumo: Math.random() * 30 + 25,
            economia_potencial: Math.random() * 15 + 5,
            eficiencia_energetica: Math.random() * 30 + 70,
            comparacao_mes_anterior: {
              variacao_percentual: (Math.random() - 0.5) * 20,
              tendencia: Math.random() > 0.5 ? 'crescente' : 'decrescente'
            },
            data_referencia: dataRef
          }
        },
        status: 200
      };
      
      return dadosFallback;
    }
  }
}

const consumosService = new ConsumosService();
export default consumosService;
